import { chowlyApi } from 'config'
import { generatePath } from 'react-router'
import client from '../chowly'
import {
  MenuMappingItemResource,
  MenuMappingSubsectionItem,
  MenuMappingSubsectionItemResource,
  MenuMappingSubsectionResource,
  TypeName,
} from '../models'
import { getMenuMappingSubsection } from './subsections'

const formatSubsectionItemPayload = ({
  attributes,
  itemId,
  subsectionId,
}: {
  attributes: MenuMappingSubsectionItem
  itemId?: string
  subsectionId?: string
}) => {
  return {
    data: {
      type: TypeName.MenuMappingSubsectionItem,
      attributes,
      relationships: {
        ...(itemId && {
          item: {
            data: {
              id: itemId,
              type: TypeName.MenuMappingItem,
            },
          },
        }),
        ...(subsectionId && {
          subsection: {
            data: {
              id: subsectionId,
              type: TypeName.MenuMappingSubsection,
            },
          },
        }),
      },
    },
  }
}

export const getMenuMappingSubsectionItemByItemId = async ({
  itemId,
  subsectionId,
}: {
  itemId: string
  subsectionId: string
}) => {
  // Get the subsection with included subsection items
  const subsectionResponse = await getMenuMappingSubsection(subsectionId, { include: 'subsection_items' })
  
  // Find the subsection item that matches both the item and subsection IDs
  const subsectionItem = (subsectionResponse.included || []).find(
    (item): item is MenuMappingSubsectionItemResource =>
      item.type === TypeName.MenuMappingSubsectionItem &&
      item.relationships.item.data.id === itemId &&
      item.relationships.subsection.data.id === subsectionId
  )

  return subsectionItem
}

export const updateMenuMappingSubsectionItem = async ({
  subsectionItemId,
  itemId,
  subsectionId,
  attributes,
}: {
  subsectionItemId: MenuMappingSubsectionItemResource['id']
  itemId: MenuMappingItemResource['id']
  subsectionId: MenuMappingSubsectionResource['id']
  attributes: MenuMappingSubsectionItem
}) => {
  const url = generatePath(chowlyApi.menuMappingSubsectionItem, { id: subsectionItemId })
  const payload = formatSubsectionItemPayload({ attributes, itemId, subsectionId })

  const { data } = await client.patch<MenuMappingSubsectionItemResource>(url, payload)

  return data
}

/**
 * Bulk update subsection items with new sort positions
 * This function updates multiple subsection items in parallel
 * @param updates - Array of updates to perform
 * @returns Array of updated subsection item resources
 */
export const bulkUpdateSubsectionItems = async (
  updates: Array<{
    subsectionItemId: string
    itemId: string
    subsectionId: string
    attributes: MenuMappingSubsectionItem
  }>
) => {
  try {
    const updatePromises = updates.map(update =>
      updateMenuMappingSubsectionItem(update)
    )

    const results = await Promise.all(updatePromises)
    return results
  } catch (error) {
    console.error('Error in bulk update of subsection items:', error)
    throw error
  }
}
